import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { ViewIcon, ViewOffIcon } from "../Icons";
import useLogin from "../hooks/useLogin";
import { prefetchTiming } from "../utils/attendanceTimingCache";
import {
  getPublicTenantConfig,
  readStoredTenantConfig,
  isLocalDevHost,
  clearStoredTenantConfig,
} from "../utils/tenantConfig";
import {
  canAccessDashboard,
  defaultDashboardTabForRole,
} from "../utils/moduleAccess";

// Home Health Agency HR Portal styles
const hhaStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob {
    animation: blob 7s infinite alternate;
  }
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  .animation-delay-4000 {
    animation-delay: 4s;
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in {
    animation: fadeIn 0.4s ease-out forwards;
  }
`;

const Login = () => {
  const { email, setEmail, password, setPassword, isSubmitting, login } =
    useLogin();

  const [showPassword, setShowPassword] = useState(false);
  const [tenantConfig, setTenantConfig] = useState(() =>
    typeof window !== "undefined" ? readStoredTenantConfig() : null,
  );
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPublicTenantConfig();
        if (!cancelled && data) setTenantConfig(data);
      } catch (e) {
        if (!cancelled) {
          toast.error(e.message || "Could not load tenant configuration");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (tenantConfig?.result && tenantConfig.result.exists === false) {
      if (isLocalDevHost()) {
        clearStoredTenantConfig();
        getPublicTenantConfig().then((data) => {
          if (data) setTenantConfig(data);
        });
        return;
      }
      navigate("/tenant-not-found", { replace: true });
    }
  }, [tenantConfig, navigate]);

  const branding = (() => {
    const result = tenantConfig?.result;
    if (!result?.exists) return { logos: [], companyName: null };
    const offices = Array.isArray(result.offices) ? result.offices : [];
    const logos = offices
      .filter((o) => o?.officeLogo)
      .slice(0, 2)
      .map((o) => ({
        src: o.officeLogo,
        alt: o.name || result.tenant?.companyName || "Office logo",
      }));
    return {
      logos,
      companyName: result.tenant?.companyName || null,
    };
  })();

  const noTenant =
    tenantConfig?.result && tenantConfig.result.exists === false;
  if (noTenant) {
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    const res = await login();

    if (res.success) {
      prefetchTiming(); // Start timing fetch before redirect so Dashboard has data sooner
      // Prefer hook payload; fall back to persisted session if shape drifts
      let sessionUser = res.user || {};
      let modules = res.modules || [];
      if (!sessionUser?.id || !modules.length) {
        try {
          const stored = JSON.parse(localStorage.getItem("currentUser") || "{}");
          if (!sessionUser?.id && stored?.id) sessionUser = stored;
          if (!modules.length && Array.isArray(stored?.modules)) {
            modules = stored.modules;
          }
        } catch {
          /* ignore */
        }
      }
      let targetPath = canAccessDashboard(sessionUser)
        ? `/?tab=${encodeURIComponent(defaultDashboardTabForRole(sessionUser))}`
        : "/";

      if (modules.length > 0) {
        // Sort modules by order to find the highest priority one
        const sortedModules = [...modules].sort(
          (a, b) => (a.order || 0) - (b.order || 0),
        );

        // Find the first module the user can actually view
        for (const module of sortedModules) {
          if (module?.permissions?.canView !== true) continue;
          switch (module.code) {
            case "DASHBOARD":
              targetPath = `/?tab=${encodeURIComponent(
                defaultDashboardTabForRole(sessionUser),
              )}`;
              break;
            case "EMPLOYEES":
              targetPath = "/employees";
              break;
            case "APPLICATION":
              // Surveyor: APPLICATION/EVALUATION are Documents deep-link only — land on HR Documents.
              targetPath =
                sessionUser?.roleId === 12 ||
                sessionUser?.role?.name === "SRVY"
                  ? "/documents"
                  : "/applications";
              break;
            case "EVALUATION":
              targetPath =
                sessionUser?.roleId === 12 ||
                sessionUser?.role?.name === "SRVY"
                  ? "/documents"
                  : "/evaluation";
              break;
            case "FORMS":
              targetPath = "/general-forms";
              break;
            case "DOCUMENT":
              targetPath = "/documents";
              break;
            case "PATIENTS":
              targetPath = "/patients";
              break;
            case "INVOICE":
              targetPath = "/invoice";
              break;
            default:
              continue;
          }
          break;
        }
      }

      // CG/CNA (and roles with dashboard access but no module canView) land on Overview
      if (targetPath === "/" && canAccessDashboard(sessionUser)) {
        targetPath = `/?tab=${encodeURIComponent(
          defaultDashboardTabForRole(sessionUser),
        )}`;
      }

      navigate(targetPath);
    } else {
      toast.error(res.message || "Login failed");
    }
  };

  return (
    <>
      <style>{hhaStyles}</style>
      <div className="min-h-screen flex bg-white font-sans text-gray-900 overflow-hidden">
        {/* Left Side - Full-bleed illustration (hidden on mobile & tablet) */}
        <div className="hidden lg:flex lg:w-1/2 relative min-h-screen overflow-hidden bg-gradient-to-br from-indigo-50 via-sky-50/90 to-indigo-100">
          <img
            src="/kornivusinfotech.png"
            alt="HR Illustration"
            className="absolute inset-0 w-full h-full object-cover object-center"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          {/* Subtle gradient overlay for depth and to separate from form */}
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/5 via-transparent to-transparent pointer-events-none" aria-hidden />
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-6 sm:p-12 xl:p-24 bg-white relative">
          {/* Subtle Mobile Background (if no left panel) */}
          <div
            className="absolute inset-0 lg:hidden pointer-events-none opacity-[0.02]"
            style={{
              backgroundImage: "radial-gradient(#000 1px, transparent 1px)",
              backgroundSize: "30px 30px",
            }}
          ></div>

          <div className="w-full max-w-[28rem] mx-auto relative z-10">
            {/* Branding from tenant config (after /config) */}
            <div className="flex items-center gap-4 mb-10 flex-wrap min-h-[3rem]">
            <img
                src="/login-logo.png"
                alt="Logo"
                className="h-12 w-auto object-contain drop-shadow-sm transition-transform hover:scale-105"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              {branding.logos.length > 0 ? (
                branding.logos.map(({ src, alt }, i) => (
                  <img
                    key={`${src}-${i}`}
                    src={src}
                    alt={alt}
                    className="h-12 w-auto max-w-[200px] object-contain drop-shadow-sm transition-transform hover:scale-105"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ))
              ) : branding.companyName ? (
                <p className="text-xl font-extrabold text-gray-900 tracking-tight">
                  {branding.companyName}
                </p>
              ) : null}
            </div>

            {/* Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight mb-2">
                Welcome back
              </h2>
              <p className="text-gray-500 text-[15px]">
                Please enter your details to sign in.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleLogin} className="space-y-5">

              {/* Email Input */}
              <div className="space-y-1.5 group">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 transition-colors group-focus-within:text-blue-600"
                >
                  Email
                </label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={(e) => setEmail(String(e.target.value || "").trim().toLowerCase())}

                    className="block w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/15 focus:border-blue-500 outline-none text-gray-900 bg-gray-50/50 hover:bg-white transition-all duration-200 placeholder-gray-400"
                    required
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-1.5 group">
                <div className="flex justify-between items-center">
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-gray-700 transition-colors group-focus-within:text-blue-600"
                  >
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/15 focus:border-blue-500 outline-none text-gray-900 bg-gray-50/50 hover:bg-white transition-all duration-200 placeholder-gray-400 pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                  >
                    {showPassword ? (
                      <ViewOffIcon className="w-5 h-5" />
                    ) : (
                      <ViewIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 px-6 rounded-xl font-bold text-[15px] shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 transform hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 mt-8"
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <span>Sign In</span>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-12 lg:mt-16 text-center">
              <p className="text-gray-400 text-sm font-medium">
                © {new Date().getFullYear()} Kornivus Infotech. All rights
                reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;